import React, { useEffect, useState } from 'react';
import { Tabs, message, Spin, Modal } from 'antd';
import { DeploymentUnitOutlined } from '@ant-design/icons';
import { useHistory, useParams } from 'react-router-dom';
import { useConfig } from '../../contexts/ConfigContext';
import { useCompany } from '../../contexts/CompanyContext';
import {
  useProductionCycle,
  useProductionCycleLogs,
} from '../../hooks/useProductionCycles';
import { useItems } from '../../hooks/useInventory';
import useAuth from '../../hooks/useAuth';
import { useSecureRequest } from '../../hooks/useSecureRequest';
import CycleLogForm from './CycleLogForm';
import CycleTraceabilityMembers from './CycleTraceabilityMembers';
import {
  aggregateCycleData,
  CycleDetailHeaderBar,
  CycleDetailInfoCard,
  CycleDetailDateFilterBar,
  CycleDetailTimelineTab,
  CycleDetailInputsTab,
  CycleDetailOutputsTab,
  CycleDetailLogDetailModal,
  CycleDetailCloseModal,
} from './cycle-detail';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
dayjs.locale('es');

const ViewCycleDetail: React.FC = () => {
  const { uid } = useParams<{ uid: string }>();
  const { config } = useConfig();
  const { company } = useCompany();
  const history = useHistory();
  const { tokenData } = useAuth.useToken(
    'authCsrfToken',
    config.session,
    config.key
  );
  const { securePost } = useSecureRequest();

  const [logModalVisible, setLogModalVisible] = useState(false);
  const [closingModalVisible, setClosingModalVisible] = useState(false);
  const [logDetailVisible, setLogDetailVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState<any>(null);

  // Filtros de fecha
  const [dateRange, setDateRange] = useState<[any, any]>([
    dayjs().subtract(3, 'month'),
    dayjs(),
  ]);

  const handleQuickFilter = (months: number | 'all' | 'week') => {
    if (months === 'all') {
      setDateRange([null, null]);
    } else if (months === 'week') {
      setDateRange([
        dayjs().subtract(7, 'day').startOf('day'),
        dayjs().endOf('day'),
      ]);
    } else {
      setDateRange([
        dayjs().subtract(months, 'month').startOf('day'),
        dayjs().endOf('day'),
      ]);
    }
  };

  const handleYearFilter = () => {
    setDateRange([dayjs().startOf('year'), dayjs().endOf('day')]);
  };

  const goToStorage = () => {
    const storageModule = (company.current as any)?.modules?.find(
      (m: any) => m.app?.toUpperCase() === 'STORAGE'
    );
    if (storageModule) {
      globalThis.location.href = `${storageModule.path}/register-entry?company=${company.current?.uid}`;
    } else {
      message.error('Módulo de almacén (Storage) no disponible.');
    }
  };

  const {
    data: cycleData,
    isLoading,
    error,
    mutate: mutateCycle,
  } = useProductionCycle(tokenData, {
    cycle_uid: uid,
    company_uid: company.current?.uid,
  });
  const cycle = cycleData?.cycle;

  const {
    data: logsData,
    isLoading: isLoadingLogs,
    mutate: mutateLogs,
  } = useProductionCycleLogs(tokenData, {
    cycle_id: cycle?.id,
    start_date: dateRange[0]
      ? dateRange[0].startOf('day').toISOString()
      : undefined,
    end_date: dateRange[1] ? dateRange[1].endOf('day').toISOString() : undefined,
  });

  const { data: inventoryData } = useItems(tokenData, {
    company: company.current?.uid,
    module: config.module,
  });
  const inventoryItems = inventoryData?.itemList || [];

  const itemsMap = React.useMemo(() => {
    const map = new Map<string, any>();
    inventoryItems.forEach((item: any) => {
      if (item.id) map.set(String(item.id), item);
      if (item.uid) map.set(String(item.uid), item);
    });
    return map;
  }, [inventoryItems]);

  useEffect(() => {
    // Solo redirigir si hay un error en el cargue inicial (sin datos previos)
    if (error && !cycleData) {
      message.error('Error al cargar detalle del ciclo productivo');
      console.log('Error al cargar detalle del ciclo productivo', error);
      history.push('/cycles');
    }
    // El backend devuelve {message: "..."} con estado 220 cuando no existe el ciclo
    // SWR lo recibe como data normal (no como error), por eso hay que verificar
    if (!isLoading && !error && cycleData && !cycleData.cycle) {
      message.error(cycleData.message || 'Ciclo productivo no encontrado');
      console.log('Ciclo productivo no encontrado', cycleData);
      history.push('/cycles');
    }
  }, [error, isLoading, cycleData, history]);

  // Mostrar spinner solo en carga inicial cuando no hay datos
  if (isLoading && !cycleData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Guard por si el render ocurre antes de que el useEffect redirija
  if (!cycle) return null;

  const logs = [...(logsData?.logs || [])].sort((a: any, b: any) => {
    const dateA = new Date(a.log_date).getTime();
    const dateB = new Date(b.log_date).getTime();
    if (dateA !== dateB) {
      return dateB - dateA;
    }
    const createdA = new Date(a.created_at || 0).getTime();
    const createdB = new Date(b.created_at || 0).getTime();
    return createdB - createdA;
  });

  const aggregatedData = aggregateCycleData(logs, itemsMap);

  const handleOpenLogDetail = (log: any) => {
    setSelectedLog(log);
    setLogDetailVisible(true);
  };

  const handleConfirmCloseCycle = async () => {
    try {
      const res = await securePost('/manufacturing/close-cycle', {
        company_uid: company.current?.uid,
        id: cycle.id,
        close_reason: 'COMPLETED',
        csrfToken: tokenData?.token?.uid,
      });
      if (res?.cycle) {
        message.success('Ciclo cerrado');
        mutateCycle();
        setClosingModalVisible(false);
      } else {
        message.error(res?.message || 'Error al cerrar');
      }
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const dateFilterBar = (
    <CycleDetailDateFilterBar
      dateRange={dateRange}
      onDateRangeChange={(dates) => setDateRange(dates)}
      onQuickFilter={handleQuickFilter}
      onYearFilter={handleYearFilter}
      isMobile={config.isMobile}
    />
  );

  return (
    <div
      style={{
        padding: config.isMobile ? '4px 0' : '16px',
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Header Bar */}
      <CycleDetailHeaderBar
        cycle={cycle}
        isMobile={config.isMobile}
        onOpenLogModal={() => setLogModalVisible(true)}
        onBack={() => history.push('/cycles')}
        onEditConfig={() => history.push(`/edit-cycle/${cycle.uid}`)}
        onGoToStorage={goToStorage}
        onOpenClosingModal={() => setClosingModalVisible(true)}
      />

      {/* Info Card */}
      <CycleDetailInfoCard cycle={cycle} isMobile={config.isMobile} />

      {/* Tabs */}
      <Tabs
        defaultActiveKey="timeline"
        items={[
          {
            key: 'timeline',
            label: 'Línea de Vida',
            children: (
              <CycleDetailTimelineTab
                logs={logs}
                cycle={cycle}
                itemsMap={itemsMap}
                isLoadingLogs={isLoadingLogs}
                isMobile={config.isMobile}
                filterBar={dateFilterBar}
                onSelectLog={handleOpenLogDetail}
              />
            ),
          },
          {
            key: 'inputs',
            label: `Costos e Insumos ${
              aggregatedData.appliedInputs.length > 0
                ? `(${aggregatedData.appliedInputs.length})`
                : aggregatedData.purchasedInputs.length > 0
                ? `(🛒 ${aggregatedData.purchasedInputs.length})`
                : ''
            }`,
            children: (
              <CycleDetailInputsTab
                aggregatedData={aggregatedData}
                isLoadingLogs={isLoadingLogs}
                isMobile={config.isMobile}
                filterBar={dateFilterBar}
                onSelectLog={handleOpenLogDetail}
              />
            ),
          },
          {
            key: 'outputs',
            label: `Producción (Outputs) ${
              aggregatedData.allOutputs.length > 0
                ? `(${aggregatedData.allOutputs.length})`
                : ''
            }`,
            children: (
              <CycleDetailOutputsTab
                aggregatedData={aggregatedData}
                unitOfMeasure={cycle.unit_of_measure}
                isLoadingLogs={isLoadingLogs}
                isMobile={config.isMobile}
                filterBar={dateFilterBar}
                onSelectLog={handleOpenLogDetail}
              />
            ),
          },
          {
            key: 'traceability',
            label: (
              <span>
                <DeploymentUnitOutlined /> Trazabilidad / Miembros
              </span>
            ),
            children: (
              <CycleTraceabilityMembers
                cycle={cycle}
                tokenData={tokenData}
                companyUid={company.current?.uid || ''}
                companyId={company.current?.id || 0}
              />
            ),
          },
        ]}
      />

      {/* Modal Registrar Evento (Log) */}
      <Modal
        title="Registrar Evento de Ciclo Productivo"
        open={logModalVisible}
        onCancel={() => setLogModalVisible(false)}
        footer={null}
        width={config.isMobile ? '100%' : 900}
        style={
          config.isMobile
            ? { top: 10, maxWidth: '100vw', padding: '0 4px' }
            : undefined
        }
        destroyOnHidden
        maskClosable={false}
      >
        <CycleLogForm
          cycle={cycle}
          onClose={() => {
            setLogModalVisible(false);
            mutateLogs();
            mutateCycle();
          }}
        />
      </Modal>

      {/* Modal Cerrar Ciclo */}
      <CycleDetailCloseModal
        visible={closingModalVisible}
        onClose={() => setClosingModalVisible(false)}
        onConfirmClose={handleConfirmCloseCycle}
        cycle={cycle}
        logs={logs}
        isMobile={config.isMobile}
      />

      {/* Modal Detalle del Evento */}
      <CycleDetailLogDetailModal
        visible={logDetailVisible}
        onClose={() => setLogDetailVisible(false)}
        selectedLog={selectedLog}
        cycle={cycle}
        itemsMap={itemsMap}
        isMobile={config.isMobile}
      />
    </div>
  );
};

export default ViewCycleDetail;
